import type { ReactNode } from 'react';

import classNames from 'classnames';

import style from './Page.module.css';

import type { BaseHeaderProps as PageBaseHeaderProps } from './BaseHeader';
import { BaseHeader } from './BaseHeader';
import type { BaseNavProps } from './BaseNav';
import { BaseNav } from './BaseNav';

export type PageLayout = 'base' | 'base-centered' | 'full-width';

export interface PageSharedProps {
  /**
   * The layout of the page.
   *
   * - `base`: a layout with headings and a navigation bar with back and close
   *   buttons.
   * - `base-centered`: a base layout with centered content and restricted main
   *   width.
   */
  layout?: PageLayout;
  /** Class name to add to the root element. */
  className?: string;
  /** Content of the `main` element. */
  children?: ReactNode;
}

export interface PageBaseLayout
  extends PageSharedProps, Omit<PageBaseHeaderProps, 'image'>, BaseNavProps {
  layout: 'base';
}

export interface PageBaseCenteredLayout
  extends PageSharedProps, PageBaseHeaderProps, BaseNavProps {
  layout: 'base-centered';
}

export interface PageFullWidthLayout extends PageSharedProps, BaseNavProps {
  layout?: 'full-width';
}

/** The props of the {@link Page} component. */
export type PageProps =
  | PageBaseLayout
  | PageBaseCenteredLayout
  | PageFullWidthLayout;

export function isCenteredLayout(
  props: PageProps
): props is PageBaseCenteredLayout {
  return props.layout === 'base-centered';
}

export function isBaseLayout(props: PageProps): props is PageBaseLayout {
  return props.layout === 'base';
}

export function isFullWidthLayout(props: PageProps): props is PageBaseLayout {
  return !props.layout || props.layout === 'full-width';
}

/**
 * Use the Page component to create the
 * {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/main_role| main landmark}
 * wrapping the content of a route or a modal.
 *
 * To create complex layouts, with footers, headers or a sidebar, wrap a Page
 * component with the {@link Layout} component.
 *
 * @see https://corgi-x.tfd.engineering/components/page
 */
export function Page(props: PageProps): JSX.Element {
  const layoutStyle = {
    [style['layout-full-width']]: isFullWidthLayout(props),
    [style['layout-base']]: isBaseLayout(props),
    [style['layout-centered']]: isCenteredLayout(props),
    [style['with-image']]: isCenteredLayout(props) && Boolean(props.image),
  };
  const className = classNames(style.root, layoutStyle, props.className);

  return (
    <div className={className}>
      <BaseNav
        onCloseClick={props.onCloseClick}
        onBackClick={props.onBackClick}
      />
      {(isBaseLayout(props) || isCenteredLayout(props)) && (
        <BaseHeader
          image={isCenteredLayout(props) ? props.image : undefined}
          heading={props.heading}
          subHeading={props.subHeading}
        />
      )}
      <main>{props.children}</main>
    </div>
  );
}
